import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Plus,
  Pencil,
  Trash2,
  Calendar,
  Clock,
  Eye,
  Tag,
  AlertTriangle,
} from "lucide-react";
import { supabase } from "../lib/supabase";
import SEO from "./SEO";
import BlogPreviewModal from "./BlogPreviewModal";
import BlogTagsModal from "./BlogTagsModal";
import { BlogPostDeletionService } from "../services/blogPostDeletionService";

export default function AdminPosts() {
  const [posts, setPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewPost, setPreviewPost] = useState<string | null>(null);
  const [tagModalPost, setTagModalPost] = useState<string | null>(null);
  const [deletingPost, setDeletingPost] = useState<string | null>(null);
  const [deletionResult, setDeletionResult] = useState<any>(null);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("blog_posts")
        .select(
          `
          *,
          blog_authors (name),
          blog_categories (name),
          blog_tag_posts (
            blog_tags (
              id,
              name
            )
          )
        `,
        )
        .eq("deleted", false)
        .order("published_at", { ascending: false });

      if (error) throw error;

      // Process posts to determine correct published status
      const processedPosts = (data || []).map((post) => {
        const today = new Date().toISOString().split("T")[0];
        const shouldBePublished =
          post.published_at && post.published_at <= today;
        return {
          ...post,
          is_published: shouldBePublished || post.is_published,
        };
      });

      setPosts(processedPosts);
    } catch (err) {
      console.error("Error fetching posts:", err);
      setError(err instanceof Error ? err.message : "Failed to load posts");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (
      !confirm(
        "Are you sure you want to delete this post? This will also remove all SEO data and update the sitemap.",
      )
    )
      return;

    try {
      setDeletingPost(id);
      setDeletionResult(null);

      console.log(`🗑️  Starting comprehensive deletion for post: ${id}`);

      const result = await BlogPostDeletionService.deleteBlogPost(id);

      if (result.success) {
        console.log(`✅ Deletion completed: ${result.message}`);
        setDeletionResult({
          type: "success",
          message: result.message,
          details: result,
        });

        // Refresh the posts list
        await fetchPosts();

        // Show success message for 5 seconds
        setTimeout(() => {
          setDeletionResult(null);
        }, 5000);
      } else {
        console.error(`❌ Deletion failed: ${result.message}`);
        setDeletionResult({
          type: "error",
          message: result.message,
          details: result,
        });
      }
    } catch (err) {
      console.error("Error deleting post:", err);
      setDeletionResult({
        type: "error",
        message: err instanceof Error ? err.message : "Failed to delete post",
        details: {
          errors: [err instanceof Error ? err.message : "Unknown error"],
        },
      });
    } finally {
      setDeletingPost(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F98B3D]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
        <p className="text-red-700">{error}</p>
      </div>
    );
  }

  return (
    <div>
      <SEO
        title="One80Labs - Manage Blog Posts"
        description="Admin interface for managing blog posts in the One80Labs content management system."
        noindex={true}
      />
      {/* Deletion Result Display */}
      {deletionResult && (
        <div
          className={`mb-4 p-4 rounded-md border-l-4 ${
            deletionResult.type === "success"
              ? "bg-green-50 border-green-400 text-green-700"
              : "bg-red-50 border-red-400 text-red-700"
          }`}
        >
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 mr-2" />
            <div>
              <p className="font-medium">{deletionResult.message}</p>
              {deletionResult.details?.seoCleanup && (
                <p className="text-sm mt-1">
                  SEO data cleared:{" "}
                  {deletionResult.details.seoCleanup.seoOptimizedAt
                    ? `Optimized on ${new Date(deletionResult.details.seoCleanup.seoOptimizedAt).toLocaleDateString()}`
                    : "No SEO data found"}
                </p>
              )}
              {deletionResult.details?.sitemapUpdate && (
                <p className="text-sm mt-1">
                  Sitemap:{" "}
                  {deletionResult.details.sitemapUpdate.success
                    ? "Updated successfully"
                    : `Update failed: ${deletionResult.details.sitemapUpdate.message}`}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="mb-6 flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Blog Posts</h1>
        <Link
          to="/admin/posts/new"
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#F98B3D] hover:bg-[#e07a2c]"
        >
          <Plus className="h-5 w-5 mr-2" />
          New Post
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {posts.map((post) => (
          <div
            key={post.id}
            className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden"
          >
            {post.image_url && (
              <img
                src={post.image_url}
                alt={post.title}
                className="w-full h-48 object-cover"
              />
            )}
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <span
                    className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      post.is_published
                        ? "bg-green-100 text-green-800"
                        : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {post.is_published ? "Published" : "Draft"}
                  </span>
                  {post.seo_optimized_at && (
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                      SEO
                    </span>
                  )}
                  {post.sitemap_updated_at && (
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                      Sitemap
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setTagModalPost(post.id)}
                    className="text-gray-600 hover:text-gray-900"
                    title="Manage Tags"
                  >
                    <Tag className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => setPreviewPost(post.slug)}
                    className="text-gray-600 hover:text-gray-900"
                  >
                    <Eye className="h-5 w-5" />
                  </button>
                  <Link
                    to={`/admin/posts/${post.id}`}
                    className="text-[#F98B3D] hover:text-[#e07a2c]"
                  >
                    <Pencil className="h-5 w-5" />
                  </Link>
                  <button
                    onClick={() => handleDelete(post.id)}
                    disabled={deletingPost === post.id}
                    className={`${
                      deletingPost === post.id
                        ? "text-gray-400 cursor-not-allowed"
                        : "text-red-600 hover:text-red-900"
                    } transition-colors`}
                    title={
                      deletingPost === post.id
                        ? "Deleting..."
                        : "Delete post (includes SEO cleanup)"
                    }
                  >
                    {deletingPost === post.id ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-600"></div>
                    ) : (
                      <Trash2 className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {post.title}
              </h3>

              <div className="flex items-center text-sm text-gray-500 mb-2">
                <Clock className="h-4 w-4 mr-1" />
                {post.read_time} min read
              </div>

              {post.published_at && (
                <div className="flex items-center text-sm text-gray-500 mb-2">
                  <Calendar className="h-4 w-4 mr-1" />
                  {new Date(post.published_at).toLocaleDateString()}
                </div>
              )}

              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="text-sm text-gray-600">
                  <span className="font-medium">Author:</span>{" "}
                  {post.blog_authors?.name}
                </div>
                <div className="text-sm text-gray-600">
                  <span className="font-medium">Category:</span>{" "}
                  {post.blog_categories?.name}
                </div>
                {post.blog_tag_posts && post.blog_tag_posts.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {post.blog_tag_posts.map(
                      (tagPost: any) =>
                        tagPost.blog_tags && (
                          <span
                            key={tagPost.blog_tags.id}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                          >
                            {tagPost.blog_tags.name}
                          </span>
                        ),
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <BlogPreviewModal
        isOpen={!!previewPost}
        onClose={() => setPreviewPost(null)}
        slug={previewPost || ""}
      />

      <BlogTagsModal
        isOpen={!!tagModalPost}
        onClose={() => setTagModalPost(null)}
        postId={tagModalPost || ""}
        onUpdate={fetchPosts}
      />
    </div>
  );
}
